import type { NavLink } from '@/types'

// Copy strings use double quotes so an apostrophe can never break the file.

export const navLinks: NavLink[] = [
    { label: "Work", href: '/#work' },
    { label: "About", href: '/#about' },
    { label: "Services", href: '/#services' },
    { label: "Process", href: '/#process' },
    { label: "Contact", href: '/#contact' }
]

export const heroIntro = "I design and build fast, responsive and animated websites that turn ideas into impactful digital products. Clean code, creative design, no compromises."

export const heroDisciplines = ["Front-end", "Full-stack", "Motion"]

export const marqueeItems = [
    "Next.js",
    "React",
    "TypeScript",
    "Node.js",
    "Tailwind CSS",
    "GSAP",
    "Three.js",
    "WebGL",
    "Framer Motion",
    "PostgreSQL",
    "Headless CMS",
    "Figma"
]

// Accent segments render in the italic serif with the brand red.
export const aboutStatement = [
    { text: "I'm Ansam, a creative full-stack developer. I blend" },
    { text: "clean code", accent: true },
    { text: "with" },
    { text: "bold design", accent: true },
    { text: "to build fast, responsive and animated web experiences that turn ideas into products people" },
    { text: "remember.", accent: true }
]

export const aboutParagraphs = [
    "For me, a great website is where engineering meets art. I care about the details most people never consciously notice: how a heading settles into place, how quickly a page answers a tap, how a layout breathes on a small screen.",
    "I work with founders, studios and brands from the first sketch to launch day, and I stay close long after. Whether it is a bold marketing site or a complex web app, the goal is the same: something that works beautifully and feels like a joy to use."
]

export const principles = [
    {
        icon: 'gauge',
        title: "Performance first",
        description: "Lean bundles, optimized media and instant interactions, measured from day one."
    },
    {
        icon: 'sparkles',
        title: "Motion with purpose",
        description: "Animation that explains, guides and delights, never motion for its own sake."
    },
    {
        icon: 'pen',
        title: "Pixel-perfect craft",
        description: "Typography, spacing and small details treated with the same care as the code."
    },
    {
        icon: 'accessibility',
        title: "Accessible by default",
        description: "Semantic markup, keyboard support and contrast that works for everyone."
    }
] as const

export const toolbox = [
    {
        group: "Front-end",
        items: ["React", "Next.js", "TypeScript", "Tailwind CSS", "Vue"]
    },
    {
        group: "Motion & 3D",
        items: ["GSAP", "Framer Motion", "Three.js", "WebGL", "Lottie"]
    },
    {
        group: "Back-end",
        items: ["Node.js", "PostgreSQL", "Prisma", "REST & GraphQL", "Supabase"]
    },
    {
        group: "Workflow",
        items: ["Figma", "Storybook", "Vercel", "Headless CMS", "Playwright"]
    }
]

export const services = [
    {
        number: '01',
        title: "Creative front-end",
        description: "Marketing sites, launches and landing pages built pixel-perfect from design, with motion that guides the eye instead of distracting it.",
        deliverables: ["Websites", "Landing pages", "Design systems"]
    },
    {
        number: '02',
        title: "Full-stack web apps",
        description: "Dashboards, booking flows, member areas and internal tools, with real databases, authentication and APIs behind clean, calm interfaces.",
        deliverables: ["Web apps", "APIs", "Dashboards"]
    },
    {
        number: '03',
        title: "Motion & interaction",
        description: "Scroll stories, micro-interactions, WebGL and 3D moments that make a brand feel alive while every page stays fast.",
        deliverables: ["Scroll stories", "WebGL & 3D", "Micro-interactions"]
    },
    {
        number: '04',
        title: "Performance, SEO & accessibility",
        description: "Core Web Vitals, semantic markup, structured data and accessible components, so your site ranks, loads in a blink and works for everyone.",
        deliverables: ["Audits", "Core Web Vitals", "WCAG"]
    }
]

export const processSteps = [
    {
        number: '01',
        title: "Discover",
        description: "We start with your goals, audience and constraints. I map the content, the key journeys and what success looks like before a single pixel moves."
    },
    {
        number: '02',
        title: "Design",
        description: "Wireframes first, then high-fidelity screens with a motion language. You see real layouts early and shape them with feedback, not guesswork."
    },
    {
        number: '03',
        title: "Build",
        description: "Clean, typed, component-driven code. You get a live preview from the first week and watch the product come together in short, visible steps."
    },
    {
        number: '04',
        title: "Launch & grow",
        description: "Performance checks, SEO, analytics and a smooth handover. After launch I stay close for refinements, new features and support."
    }
]

export const contactSteps = [
    "Share your idea, goals and timeline",
    "We talk through scope on a short call",
    "You get a clear proposal and plan"
]

export const projectTypes = ["Website", "Web app", "E-commerce", "Motion & 3D", "Something else"]
